import { forwardRef, useId, type SelectHTMLAttributes } from 'react';
import { formStyles } from '@utils/formStyles';

interface Option {
    value: string | number;
    label: string;
    disabled?: boolean;
}

interface OptionGroup {
    label: string;
    options: Option[];
}

interface FormSelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
    label?: string;
    options: Option[] | OptionGroup[];
    error?: string;
    helpText?: string;
    fullWidth?: boolean;
    size?: 'sm' | 'md' | 'lg';
    noMargin?: boolean;
}

const FormSelect = forwardRef<HTMLSelectElement, FormSelectProps>(
    (
        {
            label,
            options,
            error,
            helpText,
            fullWidth = true,
            size = 'md',
            noMargin = false,
            className = '',
            id,
            'aria-describedby': ariaDescribedBy,
            ...rest
        },
        ref
    ) => {
        const generatedId = useId();
        const selectId = id ?? generatedId;
        const helpTextId = helpText ? `${selectId}-help` : undefined;
        const errorId = error ? `${selectId}-error` : undefined;
        const describedBy = [ariaDescribedBy, helpTextId, errorId].filter(Boolean).join(' ') || undefined;

        const isOptionGroups = (opts: Option[] | OptionGroup[]): opts is OptionGroup[] => {
            return opts.length > 0 && 'options' in opts[0];
        };

        // Get size classes from our shared styles
        const sizeClasses = formStyles.sizes[size];

        const selectClasses = `${error ? formStyles.selectError : formStyles.select} ${sizeClasses} ${className}`;

        // Choose the appropriate container class based on fullWidth and noMargin
        let containerClasses;

        if (noMargin) {
            containerClasses = fullWidth ? formStyles.fullWidthContainerNoMargin : formStyles.containerNoMargin;
        } else {
            containerClasses = fullWidth ? formStyles.fullWidthContainer : formStyles.container;
        }

        return (
            <div className={containerClasses}>
                {label && (
                    <label className={formStyles.label} htmlFor={selectId}>
                        {label}
                    </label>
                )}
                <select
                    aria-describedby={describedBy}
                    aria-invalid={error ? true : undefined}
                    className={selectClasses}
                    id={selectId}
                    ref={ref}
                    {...rest}
                >
                    {isOptionGroups(options)
                        ? // Render option groups
                          options.map((group, groupIndex) => (
                              <optgroup className="font-medium" key={`group-${groupIndex}`} label={group.label}>
                                  {group.options.map((option, optIndex) => (
                                      <option
                                          className={option.disabled ? 'text-zinc-500' : ''}
                                          disabled={option.disabled}
                                          key={`option-${groupIndex}-${optIndex}`}
                                          value={option.value}
                                      >
                                          {option.label}
                                      </option>
                                  ))}
                              </optgroup>
                          ))
                        : // Render options
                          options.map((option, index) => (
                              <option
                                  className={option.disabled ? 'text-zinc-500' : ''}
                                  disabled={option.disabled}
                                  key={`option-${index}`}
                                  value={option.value}
                              >
                                  {option.label}
                              </option>
                          ))}
                </select>
                {helpText && (
                    <div className={formStyles.helpText} id={helpTextId}>
                        {helpText}
                    </div>
                )}
                {error && (
                    <div className={formStyles.errorText} id={errorId}>
                        {error}
                    </div>
                )}
            </div>
        );
    }
);

FormSelect.displayName = 'FormSelect';

export default FormSelect;
